import { HOUSE_NUMBER_PART } from './address';
export const isEmpty = (value: any) => value === null || value === undefined || value === '';

export type ValidationResult =
  | undefined
  | { key: string; values?: Record<string, string | number>; skipErrorPrefix?: boolean };

const predefinedFormats: Record<string, RegExp> = {
  default: /^[\p{L}\d\-./,()&+:!? '"\u2018-\u201F\u00AA\u00BA]+$/u,
  email: /^[\p{L}\d!#$%&'*+/=?^_`{|}~-]+(\.[\p{L}\d!#$%&'*+/=?^_`{|}~-]+)*@[\p{L}\d]+([.-][\p{L}\d]+)*\.\p{L}{2,}$/u,
  name: /^[\p{L}\-. ]+$/u,
  streetOrCity: /^[\p{L}\d\-. '\u2018-\u2019]+$/u,
  houseNumber: new RegExp(`^${HOUSE_NUMBER_PART}$`, 'u')
};

export const getFormat = (format: RegExp | keyof typeof predefinedFormats | string) => {
  if (typeof format !== 'string') {
    return format;
  } else if (format in predefinedFormats) {
    return predefinedFormats[format];
  } else {
    return new RegExp(format);
  }
};

export type LayeredValidation = {
  format: string;
  translationKey: string;
};

export type Validation = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  format?: RegExp | keyof typeof predefinedFormats | string;
  illegalTerms?: string[];
  layeredValidation?: LayeredValidation[];
};

const getValidator =
  ({ required, minLength, format, illegalTerms, layeredValidation }: Omit<Validation, 'maxLength'>) =>
  (value: string): ValidationResult => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    if (!required && isEmpty(trimmedValue)) {
      return undefined;
    } else if (isEmpty(trimmedValue)) {
      return { key: 'required' };
    } else if (minLength && trimmedValue.length < minLength) {
      return { key: 'minLength', values: { minLength } };
    } else if (format && !getFormat(format).test(trimmedValue)) {
      return { key: 'invalid' };
    } else if (illegalTerms?.some(term => value.toLowerCase().includes(term.toLowerCase()))) {
      return { key: 'invalid' };
    }

    const firstFailingLayeredValidation = layeredValidation?.find(
      validation => !getFormat(validation.format).test(trimmedValue)
    );

    if (firstFailingLayeredValidation) {
      return { key: firstFailingLayeredValidation.translationKey, skipErrorPrefix: true };
    } else {
      return undefined;
    }
  };

export default getValidator;
